// Analyse (Indsigt › Analyse, Freemi 0.51.0): dækningsbidrag pr. ordre, pakke, tilvalg, steamer, by og kunde, prognose og
// datakilder. Ren beregning uden UI – bruges ens af testversionen (DemoApi) og drift (SbAnalyse), så tallene altid regnes
// på samme måde. Databasen leverer "fakta" (én række pr. booking, materialiseret i analyse_fakta og opdateret af pg_cron)
// og små aggregater; resten regnes her. Se supabase/migrations/20261104000000_analyse.sql.
//
// Alle beløb er heltal i øre. Dækningsbidrag (DB) regnes altid ekskl. moms:
//   DB = omsætning + mersalg − løn − kørsel − materialer − udlæg − ny omgang − refusion
// Løn er de betalte timer (arbejdstiden) × sats inkl. pension og feriepenge. Den del, der ikke blev solgt, er "ubooket
// arbejdstid". Ny omgang er en reklamationsbooking (gratis ny behandling efter en klage): dens løn, kørsel og materialer
// flyttes fra den steamer, der lavede den, over på den steamer, hvis opgave der blev klaget over.
// Kilometer er et estimat ud fra postnumre (geo.ts): steamerens dag fra startadressen, kunde for kunde og hjem igen.
import { afstandKm, postnrPunkt, VEJFAKTOR, type Punkt } from './geo.ts';
import { arbejdstidPaa } from './ledige.ts';
import { plusDage } from './dato.ts';
import type { Arbejdstid, Booking, Fravaer, SaerligDag } from './typer.ts';

export { VEJFAKTOR };
/** Antagelser, indtil virksomheden selv har sat tallene (Opsætning › Omkostninger). Øre ekskl. moms. */
export const ANTAGET = { timeloen: 21500, tillaeg: 0, kmPris: 148, materialer: 3000 } as const;
export const LANG_KOERSEL_KM = 40;
export const HOEJ_RABAT = 0.25;

// ---------- Opsætning (satser) ----------
export type Drivmiddel = 'benzin' | 'diesel' | 'el';
export const DRIVMIDLER: [Drivmiddel, string, string][] = [['benzin', 'Benzin', 'liter'], ['diesel', 'Diesel', 'liter'], ['el', 'El', 'kWh']];
export type SteamerSats = {
  steamer_id: string;
  timeloen: number | null;     // øre pr. time
  tillaeg: number | null;      // pension og feriepenge i procent af timelønnen
  drivmiddel: Drivmiddel | null;
  forbrug: number | null;      // km pr. liter (el: km pr. kWh)
  energipris: number | null;   // øre pr. liter/kWh ekskl. moms
};
export type MaterialeSats = { art: 'ydelse' | 'tilvalg'; ref_id: string; beloeb: number };
export type FastOmkostning = { id: string; navn: string; beloeb: number };   // øre pr. måned ekskl. moms
export type AnalyseOpsaetning = {
  timeloen: number | null; tillaeg: number | null; kmPris: number | null; materialer: number | null;
  steamere: SteamerSats[]; materialeSatser: MaterialeSats[]; faste: FastOmkostning[];
};
export const TOM_OPSAETNING: AnalyseOpsaetning = { timeloen: null, tillaeg: null, kmPris: null, materialer: null, steamere: [], materialeSatser: [], faste: [] };

const heltal = (x: unknown) => typeof x === 'number' && Number.isInteger(x);
/** Tjek af opsætningen (samme grænser som databasen). Returnerer fejl pr. felt – tom = i orden. */
export function tjekOpsaetning(o: AnalyseOpsaetning): Record<string, string> {
  const f: Record<string, string> = {};
  const ok = (x: number | null, maks: number) => x == null || (heltal(x) && x >= 0 && x <= maks);
  if (!ok(o.timeloen, 200000)) f.timeloen = 'Timelønnen skal være mellem 0 og 2.000 kr.';
  if (o.tillaeg != null && !(o.tillaeg >= 0 && o.tillaeg <= 100)) f.tillaeg = 'Pension og feriepenge skal være mellem 0 og 100 %.';
  if (!ok(o.kmPris, 10000)) f.kmPris = 'Kørsel skal være mellem 0 og 100 kr. pr. km.';
  if (!ok(o.materialer, 1000000)) f.materialer = 'Materialer pr. ordre skal være mellem 0 og 10.000 kr.';
  for (const s of o.steamere) {
    if (!ok(s.timeloen, 200000)) f['st:' + s.steamer_id] = 'Timelønnen skal være mellem 0 og 2.000 kr.';
    else if (s.tillaeg != null && !(s.tillaeg >= 0 && s.tillaeg <= 100)) f['st:' + s.steamer_id] = 'Pension og feriepenge skal være mellem 0 og 100 %.';
    else if (s.forbrug != null && !(s.forbrug > 0 && s.forbrug <= 100)) f['st:' + s.steamer_id] = 'Forbruget skal være mellem 0 og 100 km pr. liter/kWh.';
    else if (!ok(s.energipris, 10000)) f['st:' + s.steamer_id] = 'Prisen skal være mellem 0 og 100 kr. pr. liter/kWh.';
  }
  for (const m of o.materialeSatser) if (!ok(m.beloeb, 1000000)) f[`mat:${m.art}:${m.ref_id}`] = 'Beløbet skal være mellem 0 og 10.000 kr.';
  o.faste.forEach((x, i) => {
    if (!x.navn.trim() || x.navn.trim().length > 80) f['fast:' + i] = 'Skriv et navn (højst 80 tegn).';
    else if (!ok(x.beloeb, 100000000)) f['fast:' + i] = 'Beløbet skal være mellem 0 og 1 mio. kr. pr. måned.';
  });
  if (o.faste.length > 50) f.faste = 'Højst 50 faste omkostninger.';
  return f;
}

/** Satserne, som beregningen bruger: virksomhedens egne – ellers antagelserne. */
export type Satser = {
  /** øre pr. time inkl. pension og feriepenge */ loen: (steamer_id: string | null) => number;
  /** øre pr. km ekskl. moms */ km: (steamer_id: string | null) => number;
  materialeYdelse: (id: string) => number; materialeTilvalg: (id: string) => number;
  loenAntaget: (steamer_id: string | null) => boolean; kmAntaget: (steamer_id: string | null) => boolean; materialerAntaget: boolean;
  faste: number;   // øre pr. måned
};
export function satser(o: AnalyseOpsaetning): Satser {
  const st = new Map(o.steamere.map((s) => [s.steamer_id, s]));
  const mat = new Map(o.materialeSatser.map((m) => [m.art + ':' + m.ref_id, m.beloeb]));
  const timeloen = (id: string | null) => (id ? st.get(id)?.timeloen : null) ?? o.timeloen ?? ANTAGET.timeloen;
  const tillaeg = (id: string | null) => (id ? st.get(id)?.tillaeg : null) ?? o.tillaeg ?? ANTAGET.tillaeg;
  const bil = (id: string | null) => { const s = id ? st.get(id) : null; return s?.drivmiddel && s.forbrug && s.energipris != null ? s.energipris / s.forbrug : null; };
  return {
    loen: (id) => timeloen(id) * (1 + tillaeg(id) / 100),
    km: (id) => bil(id) ?? o.kmPris ?? ANTAGET.kmPris,
    materialeYdelse: (id) => mat.get('ydelse:' + id) ?? o.materialer ?? ANTAGET.materialer,
    materialeTilvalg: (id) => mat.get('tilvalg:' + id) ?? 0,
    loenAntaget: (id) => !((!!id && st.get(id)?.timeloen != null) || o.timeloen != null),
    kmAntaget: (id) => bil(id) == null && o.kmPris == null,
    materialerAntaget: o.materialer == null && !o.materialeSatser.length,
    faste: o.faste.reduce((s, x) => s + x.beloeb, 0),
  };
}

// ---------- Fakta: én række pr. booking ----------
export type Fakta = {
  id: number; dato: string; status: Booking['status']; steamer_id: string | null; ydelse_id: string; postnr: string; by: string; kunde_id: string;
  reklamation_for: number | null; medlem: boolean; kupon: string | null; ny_kunde: boolean; udeblev: boolean;
  pris: number; moms: number;            // hele bookingen ekskl. moms og momsen (inkl. tilvalg, mersalg og gebyrer)
  mersalg: number; mersalg_moms: number; // godkendt mersalg (ekstra tilvalg efter bestillingen) – indgår i pris/moms
  gebyr: number; rabat: number;          // ekskl. moms
  tilvalg: [string, number, number][];   // [tilvalg, antal, pris ekskl. moms]
  minutter: number; km: number;          // planlagt tid og estimeret kørsel til kunden (sidste opgave: også hjem)
  oprettet: string;
};

/** Bookinger, der indgår i Analyse: alt med en dato undtagen aflyste – men udeblevne kunder (kørslen var forgæves) tæller. */
export const MED_I_ANALYSE = (b: Pick<Booking, 'status' | 'dato' | 'felter'>) =>
  !!b.dato && (['faerdig', 'i_gang', 'godkendt', 'afventer', 'mangler_tid'].includes(b.status) || (b.status === 'aflyst' && b.felter?.udeblev === 'ja'));
/** Udført arbejde (tæller i omsætningen): færdige og igangværende, godkendte på en dag, der er gået – og udeblevne. */
export const udfoert = (f: Pick<Fakta, 'status' | 'dato' | 'udeblev'>, idag: string) =>
  f.udeblev || f.status === 'faerdig' || f.status === 'i_gang' || (f.status === 'godkendt' && f.dato <= idag);
/** Booket til en dag, der ikke er gået endnu. */
export const booket = (f: Pick<Fakta, 'status' | 'dato' | 'udeblev'>, idag: string) => !f.udeblev && f.dato > idag && ['godkendt', 'afventer', 'mangler_tid'].includes(f.status);

/** Dansk dato for et tidspunkt (et morgen-tidspunkt i UTC er samme dag i Danmark – så slipper vi for at formatere). */
const DANSK_DATO = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Copenhagen' });
const rundKm = (x: number) => Math.round(x * 10) / 10;
const ben = (a: Punkt | null, b: Punkt | null) => (a && b ? afstandKm(a, b) * VEJFAKTOR : 0);

/** Fakta for bookingerne (samme regler som pixie_analyse_opdater i databasen – tests/db/analyse.test.ts sammenligner dem). */
export function lavFakta(a: {
  bookinger: Booking[]; steamere: { id: string; start_postnr: string }[]; ordrer: { id: number; kupon?: string | null }[];
}): Fakta[] {
  const med = a.bookinger.filter(MED_I_ANALYSE);
  const start = new Map(a.steamere.map((s) => [s.id, postnrPunkt(s.start_postnr)]));
  const kupon = new Map(a.ordrer.map((o) => [o.id, o.kupon ?? null]));
  // Kørsel: steamerens dag i rækkefølge – fra start, kunde for kunde, og hjem efter den sidste
  const km = new Map<number, number>();
  const dage = new Map<string, Booking[]>();
  for (const b of med) if (b.steamer_id) { const n = b.steamer_id + '|' + b.dato; (dage.get(n) ?? dage.set(n, []).get(n)!).push(b); }
  for (const l of dage.values()) {
    l.sort((x, y) => (x.start ?? 99999) - (y.start ?? 99999) || x.id - y.id);
    const hjem = start.get(l[0].steamer_id!) ?? null;
    let fra = hjem;
    l.forEach((b, i) => {
      const p = postnrPunkt(b.postnr);
      let x = ben(fra ?? p, p);
      if (i === l.length - 1) x += ben(p, hjem ?? p);
      km.set(b.id, rundKm(x));
      fra = p;
    });
  }
  // Første booking pr. kunde (ikke aflyste) = ny kunde
  const foerste = new Map<string, Booking>();
  for (const b of a.bookinger) if (b.status !== 'aflyst' && b.dato) {
    const f = foerste.get(b.kunde_id);
    if (!f || b.dato < f.dato || (b.dato === f.dato && b.id < f.id)) foerste.set(b.kunde_id, b);
  }
  return med.map((b): Fakta => {
    const ms = (b.ekstra ?? []).filter((e) => e.status === 'godkendt' && !(e.type === 'aendring' && e.beloeb < 0));
    return {
      id: b.id, dato: b.dato, status: b.status, steamer_id: b.steamer_id, ydelse_id: b.ydelse_id, postnr: b.postnr, by: b.by, kunde_id: b.kunde_id,
      reklamation_for: b.reklamation_for ?? null, medlem: !!b.medlemskab_id, kupon: (b.ordre_id != null ? kupon.get(b.ordre_id) : null) ?? null,
      ny_kunde: foerste.get(b.kunde_id)?.id === b.id, udeblev: b.felter?.udeblev === 'ja',
      pris: b.pris, moms: b.moms, mersalg: ms.reduce((s, e) => s + e.beloeb, 0), mersalg_moms: ms.reduce((s, e) => s + e.moms, 0),
      gebyr: (b.gebyrer ?? []).reduce((s, g) => s + g.beloeb, 0), rabat: b.rabat ?? 0,
      tilvalg: (b.tilvalg ?? []).map((t) => [t.tilvalg_id, t.antal, t.pris] as [string, number, number]),
      minutter: b.start != null && b.slut != null ? Math.max(0, b.slut - b.start) : 0, km: km.get(b.id) ?? 0,
      oprettet: b.oprettet ? (/^\d{4}-\d\d-\d\dT0[5-9]/.test(b.oprettet) ? b.oprettet.slice(0, 10) : DANSK_DATO.format(new Date(b.oprettet))) : b.dato,
    };
  });
}

// ---------- Rå data til en periode (fra databasen eller testversionen) ----------
export type BetalingRaa = { tilTiden: number; dage1: number; dage15: number; dage30: number; snitDage: number | null };
export type RykkerRaa = { rykker1: number; rykker2: number; klar: number; udestaaende30: number; kunder30: number; hosRykker2: number };
export type AnalyseRaa = {
  fra: string; til: string; idag: string;
  fakta: Fakta[];
  refusioner: { booking_id: number | null; steamer_id: string | null; beloeb: number }[];   // ekskl. moms (skadesager.refusion_beloeb)
  udlaeg: { steamer_id: string | null; kategori: string; beloeb: number }[];                // inkl. moms
  steamere: { id: string; navn: string; aktiv: boolean; farve: string }[];
  arbejdstider: Arbejdstid[]; saerligeDage: SaerligDag[]; fravaer: Fravaer[]; lukkedage: string[];
  ydelser: { id: string; navn: string }[]; tilvalg: { id: string; navn: string; varighed: number }[];
  betaling: BetalingRaa; rykker: RykkerRaa;
  medlemmer: { aktiveStart: number; opsagt: number };
};
/** Omsætning pr. postnummer og by i en sammenligningsperiode (forrige periode / samme periode sidste år). */
export type Sammenlign = { fra: string; til: string; byer: { postnr: string; by: string; oms: number; moms: number; antal: number }[] };
export type AnalyseHistorik = {
  idag: string;
  maaneder: { maaned: string; oms: number; moms: number; antal: number }[];               // udført pr. måned (YYYY-MM)
  booket: { maaned: string; oms: number; moms: number; antal: number; medlem: number }[];  // denne og de næste måneder: udført + booket
  kendt: { maaned: string; kendt: number; endelig: number }[];                            // de seneste hele måneder (ikke medlemmer)
  medlemForventet: { maaned: string; oms: number }[];                                     // aktive medlemskabers forventede besøg
  churn: { maaned: string; opsagt: number; aktive: number }[];
  klv: { alle: number; medlem: number; oevrige: number };                                 // omsætning pr. kunde ekskl. moms
  byer: { antal: number; fra: string | null };
  status: AnalyseStatus;
};
export type AnalyseStatus = {
  bookinger: number; bookingerFra: string | null; mersalg: number; klager: number; nyOmgang: number; refusioner: number;
  betalinger: number; rykkere: number; medlemskaber: number; medlemFra: string | null; arbejdstider: number; udlaeg: number;
  ankommet: number; underskrift: number;
  steamere: number; steamereLoen: number; steamereBil: number; ydelser: number; ydelserMateriale: number; faste: number;
};

// ---------- Beregningen ----------
export type OrdreDB = {
  f: Fakta; oms: number; omsInkl: number; mersalg: number; mersalgInkl: number;
  loen: number; loenBasis: number; loenTilvalg: number; koersel: number; matBasis: number; matTilvalg: number; materialer: number;
  db: number;                 // ordrens eget DB (før klager)
  nyOmgang: number; nyOmgangAntal: number; refusion: number; resultat: number;   // resultat = db − ny omgang − refusion
};
export type Linje = { id: string; navn: string; antaget?: boolean };
export type SteamerRaekke = {
  id: string; navn: string; farve: string; opgaver: number; booket: number | null;   // andel af den betalte tid, der er booket
  timer: number; sats: number; oms: number; omsInkl: number; mersalg: number;
  loen: number; loenNyOmgang: number; koersel: number; km: number; koerselNyOmgang: number; kmNyOmgang: number; materialer: number; udlaeg: number;
  nyOmgang: number; nyOmgangAntal: number; refusion: number; db: number; dbTime: number | null;
  loenAntaget: boolean; kmAntaget: boolean; mangeKlager: boolean;
};
export type PakkeRaekke = { id: string; navn: string; ordrer: number; pris: number; loen: number; koersel: number; materialer: number; dbOrdre: number; db: number };
export type TilvalgRaekke = { id: string; navn: string; solgt: number; pris: number; loen: number; materialer: number; dbStk: number; db: number };
export type RabatRaekke = { kode: string; brugt: number; rabat: number; rabatInkl: number; nye: number; dbOrdre: number };
export type ByRaekke = { by: string; punkt: Punkt | null; antal: number; oms: number; omsInkl: number; km: number; dbOrdre: number; db: number; foer: number | null };
export type TabsOrdre = { id: number; postnr: string; by: string; pakke: string; steamer: string; dbNormalt: number; tab: number; resultat: number; aarsager: string[] };
export type TabsAarsag = { id: 'ny_omgang' | 'rabat' | 'koersel' | 'refusion' | 'udeblev'; navn: string; beloeb: number; ordrer: number };

export type Analyse = {
  fra: string; til: string; dage: number;
  ordrer: number; oms: number; omsInkl: number; moms: number; mersalg: number; mersalgInkl: number;
  loen: number; loenBooket: number; ubooket: number; ubookedeTimer: number; koersel: number; km: number; materialer: number;
  udlaeg: number; udlaegInkl: number; refusion: number; refusionInkl: number; nyOmgang: number; nyOmgangAntal: number;
  gebyr: number; db: number; faste: number;
  /** Fra omsætning til DB (inkl. moms-visningen har momsen som egen linje). */
  vandfald: { omsInkl: number; refusionInkl: number; momsNetto: number; oms: number; refusion: number; loen: number; koersel: number; materialer: number; udlaeg: number; db: number };
  steamere: SteamerRaekke[];
  pakker: PakkeRaekke[]; nyOmgangRaekke: PakkeRaekke | null; tilvalg: TilvalgRaekke[]; tilvalgDb: number; afrunding: number;
  rabatter: RabatRaekke[]; udenKodeDbOrdre: number | null;
  byer: ByRaekke[];
  tabsordrer: TabsOrdre[]; tabAntal: number; tabSum: number; aarsager: TabsAarsag[];
  kunder: { kunde_id: string; postnr: string; by: string; ordrer: number; omsInkl: number; db: number; aarsager: string[] }[];
  antaget: { loen: boolean; km: boolean; materialer: boolean; faste: boolean };
  betaling: BetalingRaa; rykker: RykkerRaa; churn: number | null; medlemmer: AnalyseRaa['medlemmer'];
};

const sum = <T>(l: T[], f: (x: T) => number) => l.reduce((s, x) => s + f(x), 0);
const div = (a: number, b: number) => (b ? Math.round(a / b) : 0);
const exMoms = (inkl: number) => Math.round(inkl / 1.25);
/** "København Ø" → "København", "Aarhus C" → "Aarhus" (bydelenes bogstaver samles under byen). */
export const byNavn = (by: string) => by.trim().replace(/\s+(Ø|V|K|N|S|C|NV|SV|SØ|NØ|Ø\.|V\.)$/u, '') || '–';
/** Kort kundenummer til visning uden navn (fx "Kunde 10482"). */
export const kundeNr = (id: string) => String([...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % 90000 + 10000);

/** Den betalte arbejdstid pr. steamer i perioden (op til i dag), i minutter. */
export function betalteMinutter(r: Pick<AnalyseRaa, 'fra' | 'til' | 'idag' | 'steamere' | 'arbejdstider' | 'saerligeDage' | 'fravaer' | 'lukkedage'>, aktive: Set<string>): Map<string, number> {
  const ud = new Map<string, number>();
  const slut = r.til < r.idag ? r.til : r.idag;
  for (const s of r.steamere) {
    if (!s.aktiv && !aktive.has(s.id)) continue;
    let m = 0;
    for (let d = r.fra; d <= slut; d = plusDage(d, 1)) {
      const a = arbejdstidPaa({ steamer: { id: s.id, aktiv: true } as never, dato: d, arbejdstider: r.arbejdstider, fravaer: r.fravaer, saerligeDage: r.saerligeDage, lukkedage: r.lukkedage });
      if (a) m += Math.max(0, a.slut - a.start);
    }
    if (m) ud.set(s.id, m);
  }
  return ud;
}

/** Ordrens dækningsbidrag (ekskl. moms) med de satser, der gælder. */
export function ordreDB(f: Fakta, s: Satser, tilvalgMin: (id: string) => number): Omit<OrdreDB, 'nyOmgang' | 'nyOmgangAntal' | 'refusion' | 'resultat'> {
  const oms = f.pris - f.mersalg, mersalg = f.mersalg;
  const omsInkl = f.pris + f.moms - f.mersalg - f.mersalg_moms, mersalgInkl = f.mersalg + f.mersalg_moms;
  const rate = s.loen(f.steamer_id);
  const loen = Math.round(f.minutter * rate / 60);
  const tvMin = Math.min(f.minutter, sum(f.tilvalg, ([id, n]) => tilvalgMin(id) * n));
  const loenTilvalg = f.minutter ? Math.round(loen * tvMin / f.minutter) : 0;
  const koersel = Math.round(f.km * s.km(f.steamer_id));
  const matBasis = f.udeblev ? 0 : s.materialeYdelse(f.ydelse_id);
  const matTilvalg = f.udeblev ? 0 : sum(f.tilvalg, ([id, n]) => s.materialeTilvalg(id) * n);
  const materialer = matBasis + matTilvalg;
  return { f, oms, omsInkl, mersalg, mersalgInkl, loen, loenBasis: loen - loenTilvalg, loenTilvalg, koersel, matBasis, matTilvalg, materialer,
    db: oms + mersalg - loen - koersel - materialer };
}

/** Hele analysen for en periode. `foer`: forrige periode (til "mod september" pr. by). */
export function beregnAnalyse(r: AnalyseRaa, o: AnalyseOpsaetning, foer?: Sammenlign | null): Analyse {
  const s = satser(o);
  const tvMin = new Map(r.tilvalg.map((t) => [t.id, t.varighed]));
  const tilvalgMin = (id: string) => tvMin.get(id) ?? 0;
  const navn = new Map(r.steamere.map((x) => [x.id, x]));
  const ydNavn = (id: string) => r.ydelser.find((y) => y.id === id)?.navn ?? id;
  const udf = r.fakta.filter((f) => f.dato >= r.fra && f.dato <= r.til && udfoert(f, r.idag));
  const alle = udf.map((f) => ({ ...ordreDB(f, s, tilvalgMin), nyOmgang: 0, nyOmgangAntal: 0, refusion: 0, resultat: 0 }) as OrdreDB);
  const paaId = new Map(alle.map((x) => [x.f.id, x]));
  const rekl = alle.filter((x) => x.f.reklamation_for != null);
  const ordrer = alle.filter((x) => x.f.reklamation_for == null);
  // Ny omgang og refusion lægges på den ordre, der blev klaget over (findes den ikke i perioden: kun på steameren)
  const nyOmgangSteamer = new Map<string, { kr: number; n: number }>();
  for (const x of rekl) {
    const org = paaId.get(x.f.reklamation_for!);
    const kost = x.loen + x.koersel + x.materialer - x.oms - x.mersalg;
    if (org) { org.nyOmgang += kost; org.nyOmgangAntal++; }
    const st = org?.f.steamer_id ?? x.f.steamer_id ?? '';
    const v = nyOmgangSteamer.get(st) ?? { kr: 0, n: 0 }; v.kr += kost; v.n++; nyOmgangSteamer.set(st, v);
  }
  const refusionSteamer = new Map<string, number>();
  for (const x of r.refusioner) {
    const ex = x.beloeb;
    const org = x.booking_id != null ? paaId.get(x.booking_id) : undefined;
    if (org) org.refusion += ex;
    const st = x.steamer_id ?? org?.f.steamer_id ?? '';
    refusionSteamer.set(st, (refusionSteamer.get(st) ?? 0) + ex);
  }
  for (const x of ordrer) x.resultat = x.db - x.nyOmgang - x.refusion;

  // Løn: de betalte timer × sats (mindst den bookede tid)
  const booketMin = new Map<string, number>();
  for (const x of alle) if (x.f.steamer_id) booketMin.set(x.f.steamer_id, (booketMin.get(x.f.steamer_id) ?? 0) + x.f.minutter);
  const betalt = betalteMinutter(r, new Set(booketMin.keys()));
  const steamerIds = [...new Set([...betalt.keys(), ...booketMin.keys()])];
  const loenSteamer = new Map(steamerIds.map((id) => [id, Math.round(Math.max(betalt.get(id) ?? 0, booketMin.get(id) ?? 0) * s.loen(id) / 60)]));
  const loen = sum([...loenSteamer.values()], (x) => x);
  const loenBooket = sum(alle, (x) => x.loen);
  const udenSteamerLoen = sum(alle.filter((x) => !x.f.steamer_id), (x) => x.loen);
  const loenI = loen + udenSteamerLoen;
  const ubooket = loenI - loenBooket;
  const ubookedeTimer = sum(steamerIds, (id) => Math.max(0, (betalt.get(id) ?? 0) - (booketMin.get(id) ?? 0))) / 60;
  const udlaegMed = r.udlaeg.filter((u) => u.kategori !== 'braendstof' && u.kategori !== 'materialer');   // dækket af kørsel og materialer
  const udlaegInkl = sum(udlaegMed, (u) => u.beloeb), udlaeg = sum(udlaegMed, (u) => exMoms(u.beloeb));
  const refusion = sum(r.refusioner, (x) => x.beloeb), refusionInkl = sum(r.refusioner, (x) => Math.round(x.beloeb * 1.25));
  const oms = sum(alle, (x) => x.oms), omsInkl = sum(alle, (x) => x.omsInkl), mersalg = sum(alle, (x) => x.mersalg), mersalgInkl = sum(alle, (x) => x.mersalgInkl);
  const koersel = sum(alle, (x) => x.koersel), materialer = sum(alle, (x) => x.materialer);
  const db = oms + mersalg - loenI - koersel - materialer - udlaeg - refusion;
  const nyOmgang = sum(rekl, (x) => x.loen + x.koersel + x.materialer - x.oms - x.mersalg);

  // Pr. steamer
  const udlaegSt = new Map<string, number>();
  for (const u of udlaegMed) udlaegSt.set(u.steamer_id ?? '', (udlaegSt.get(u.steamer_id ?? '') ?? 0) + exMoms(u.beloeb));
  const ids = [...new Set([...steamerIds, ...alle.map((x) => x.f.steamer_id ?? ''), ...udlaegSt.keys(), ...refusionSteamer.keys(), ...nyOmgangSteamer.keys()])];
  const nyN = sum([...nyOmgangSteamer.values()], (v) => v.n), aktiveMedOpg = Math.max(1, steamerIds.length);
  const steamere: SteamerRaekke[] = ids.map((id) => {
    const mine = alle.filter((x) => (x.f.steamer_id ?? '') === id), mineRekl = mine.filter((x) => x.f.reklamation_for != null);
    const st = id ? navn.get(id) : null;
    const loenHel = id ? loenSteamer.get(id) ?? 0 : udenSteamerLoen;
    const loenNy = sum(mineRekl, (x) => x.loen), koerselNy = sum(mineRekl, (x) => x.koersel), matNy = sum(mineRekl, (x) => x.materialer);
    const ny = nyOmgangSteamer.get(id) ?? { kr: 0, n: 0 };
    const rad = {
      id, navn: st?.navn ?? (id ? 'Tidligere steamer' : 'Uden steamer / kontoret'), farve: st?.farve ?? '#8A949E',
      opgaver: mine.length - mineRekl.length, booket: betalt.get(id) ? Math.min(1, (booketMin.get(id) ?? 0) / betalt.get(id)!) : null,
      timer: Math.max(betalt.get(id) ?? 0, booketMin.get(id) ?? 0) / 60, sats: s.loen(id || null),
      // Omsætning på en ny omgang (sjældent – den er gratis) regnes ind i ny omgang, så rækkerne summer til det samme som i alt
      oms: sum(mine, (x) => x.oms) - sum(mineRekl, (x) => x.oms), omsInkl: sum(mine, (x) => x.omsInkl) - sum(mineRekl, (x) => x.omsInkl),
      mersalg: sum(mine, (x) => x.mersalg) - sum(mineRekl, (x) => x.mersalg),
      loen: loenHel - loenNy, loenNyOmgang: loenNy, koersel: sum(mine, (x) => x.koersel) - koerselNy, km: rundKm(sum(mine, (x) => x.f.km)),
      koerselNyOmgang: koerselNy, kmNyOmgang: rundKm(sum(mineRekl, (x) => x.f.km)), materialer: sum(mine, (x) => x.materialer) - matNy,
      udlaeg: udlaegSt.get(id) ?? 0, nyOmgang: ny.kr, nyOmgangAntal: ny.n, refusion: refusionSteamer.get(id) ?? 0, db: 0, dbTime: null as number | null,
      loenAntaget: !!id && s.loenAntaget(id), kmAntaget: !!id && s.kmAntaget(id), mangeKlager: ny.n >= 3 && ny.n > 2 * nyN / aktiveMedOpg,
    };
    rad.db = rad.oms + rad.mersalg - rad.loen - rad.koersel - rad.materialer - rad.udlaeg - rad.nyOmgang - rad.refusion;
    rad.dbTime = rad.timer ? Math.round(rad.db / rad.timer) : null;
    return rad;
  }).filter((x) => x.opgaver || x.timer || x.udlaeg || x.refusion || x.nyOmgang)
    .sort((a, b) => (a.id === '' ? 1 : 0) - (b.id === '' ? 1 : 0) || b.oms - a.oms || a.navn.localeCompare(b.navn, 'da'));

  // Pr. pakke, ny omgang, tilvalg og gebyrer
  const pakker = grupper(ordrer, (x) => x.f.ydelse_id).map(([id, l]): PakkeRaekke => {
    const pris = sum(l, (x) => x.oms + x.mersalg - x.f.gebyr - sum(x.f.tilvalg, (t) => t[2]));
    const lo = sum(l, (x) => x.loenBasis), ko = sum(l, (x) => x.koersel), ma = sum(l, (x) => x.matBasis);
    const d = pris - lo - ko - ma;
    return { id, navn: ydNavn(id), ordrer: l.length, pris: div(pris, l.length), loen: div(lo, l.length), koersel: div(ko, l.length), materialer: div(ma, l.length), dbOrdre: div(d, l.length), db: d };
  }).sort((a, b) => b.db - a.db);
  const nyOmgangRaekke: PakkeRaekke | null = rekl.length ? (() => {
    const pris = sum(rekl, (x) => x.oms + x.mersalg), lo = sum(rekl, (x) => x.loen), ko = sum(rekl, (x) => x.koersel), ma = sum(rekl, (x) => x.materialer);
    return { id: 'ny_omgang', navn: 'Ny omgang (klager)', ordrer: rekl.length, pris: div(pris, rekl.length), loen: div(lo, rekl.length), koersel: div(ko, rekl.length), materialer: div(ma, rekl.length), dbOrdre: div(pris - lo - ko - ma, rekl.length), db: pris - lo - ko - ma };
  })() : null;
  const tvL = new Map<string, { solgt: number; pris: number; loen: number; mat: number }>();
  for (const x of ordrer) {
    const totMin = sum(x.f.tilvalg, ([id, n]) => tilvalgMin(id) * n);
    for (const [id, n, p] of x.f.tilvalg) {
      const v = tvL.get(id) ?? { solgt: 0, pris: 0, loen: 0, mat: 0 };
      v.solgt += n; v.pris += p; v.loen += totMin ? Math.round(x.loenTilvalg * tilvalgMin(id) * n / totMin) : 0; v.mat += s.materialeTilvalg(id) * n;
      tvL.set(id, v);
    }
  }
  const tilvalg = [...tvL].map(([id, v]): TilvalgRaekke => ({ id, navn: r.tilvalg.find((t) => t.id === id)?.navn ?? id, solgt: v.solgt, pris: div(v.pris, v.solgt), loen: div(v.loen, v.solgt), materialer: div(v.mat, v.solgt),
    dbStk: div(v.pris - v.loen - v.mat, v.solgt), db: v.pris - v.loen - v.mat })).sort((a, b) => b.db - a.db);
  const tilvalgDb = sum(tilvalg, (t) => t.db);
  const gebyr = sum(ordrer, (x) => x.f.gebyr);
  const afrunding = db - (sum(pakker, (p) => p.db) + (nyOmgangRaekke?.db ?? 0) + tilvalgDb + gebyr - ubooket - refusion - udlaeg);

  // Rabatkoder
  const medKode = ordrer.filter((x) => x.f.kupon), udenKode = ordrer.filter((x) => !x.f.kupon && !x.f.medlem);
  const rabatter = grupper(medKode, (x) => x.f.kupon!.toUpperCase()).map(([kode, l]): RabatRaekke => ({
    kode, brugt: l.length, rabat: sum(l, (x) => x.f.rabat), rabatInkl: Math.round(sum(l, (x) => x.f.rabat) * 1.25), nye: l.filter((x) => x.f.ny_kunde).length, dbOrdre: div(sum(l, (x) => x.resultat), l.length),
  })).sort((a, b) => b.brugt - a.brugt);

  // Geografi
  const foerBy = new Map<string, number>();
  for (const b of foer?.byer ?? []) foerBy.set(byNavn(b.by), (foerBy.get(byNavn(b.by)) ?? 0) + b.oms);
  const byer = grupper(ordrer, (x) => byNavn(x.f.by)).map(([by, l]): ByRaekke => {
    const pkt = l.map((x) => postnrPunkt(x.f.postnr)).filter((p): p is Punkt => !!p);
    const o = sum(l, (x) => x.oms + x.mersalg), d = sum(l, (x) => x.resultat);
    const f = foerBy.get(by);
    return { by, punkt: pkt.length ? { lat: sum(pkt, (p) => p.lat) / pkt.length, lng: sum(pkt, (p) => p.lng) / pkt.length } : null, antal: l.length, oms: o, omsInkl: sum(l, (x) => x.omsInkl + x.mersalgInkl),
      km: Math.round(sum(l, (x) => x.f.km) / l.length), dbOrdre: div(d, l.length), db: d, foer: f ? (o - f) / f : null };
  }).sort((a, b) => b.antal - a.antal || b.oms - a.oms);

  // Tab
  const normalt = new Map(pakker.map((p) => [p.id, p.dbOrdre]));
  const aarsagerFor = (x: OrdreDB) => {
    const a: string[] = [];
    if (x.nyOmgangAntal) a.push(`${x.nyOmgangAntal} ny ${x.nyOmgangAntal === 1 ? 'omgang' : 'omgange'}`);
    if (x.refusion) a.push('refusion');
    if (x.f.rabat && x.f.rabat / (x.f.pris + x.f.rabat) >= HOEJ_RABAT) a.push(`rabat ${Math.round(x.f.rabat / (x.f.pris + x.f.rabat) * 100)} %`);
    if (x.f.km > LANG_KOERSEL_KM) a.push(`${Math.round(x.f.km)} km`);
    if (x.f.udeblev) a.push('ikke hjemme');
    return a;
  };
  const tab = ordrer.filter((x) => x.resultat < 0).sort((a, b) => a.resultat - b.resultat);
  const tabsordrer = tab.slice(0, 8).map((x): TabsOrdre => {
    const n = normalt.get(x.f.ydelse_id) ?? 0;
    return { id: x.f.id, postnr: x.f.postnr, by: x.f.by, pakke: ydNavn(x.f.ydelse_id), steamer: (x.f.steamer_id && navn.get(x.f.steamer_id)?.navn) || '–', dbNormalt: n, tab: n - x.resultat, resultat: x.resultat, aarsager: aarsagerFor(x) };
  });
  const hoejRabat = ordrer.filter((x) => x.f.rabat && x.f.rabat / (x.f.pris + x.f.rabat) >= HOEJ_RABAT);
  const lang = ordrer.filter((x) => x.f.km > LANG_KOERSEL_KM);
  const udeblevne = ordrer.filter((x) => x.f.udeblev);
  const aarsager: TabsAarsag[] = ([
    { id: 'ny_omgang', navn: 'Klage – ny omgang', beloeb: nyOmgang, ordrer: rekl.length },
    { id: 'rabat', navn: 'Høj rabat (25 % eller mere)', beloeb: sum(hoejRabat, (x) => x.f.rabat), ordrer: hoejRabat.length },
    { id: 'koersel', navn: `Lang kørsel (over ${LANG_KOERSEL_KM} km)`, beloeb: sum(lang, (x) => x.koersel), ordrer: lang.length },
    { id: 'refusion', navn: 'Klage – refusion', beloeb: refusion, ordrer: r.refusioner.length },
    { id: 'udeblev', navn: 'Kunde ikke hjemme / udeblevet', beloeb: sum(udeblevne, (x) => x.loen + x.koersel - x.oms - x.mersalg), ordrer: udeblevne.length },
  ] as TabsAarsag[]).filter((x) => x.ordrer > 0 && x.beloeb > 0).sort((a, b) => b.beloeb - a.beloeb);
  const kunder = grupper(ordrer, (x) => x.f.kunde_id).map(([kunde_id, l]) => ({ kunde_id, postnr: l[0].f.postnr, by: l[0].f.by, ordrer: l.length,
    omsInkl: sum(l, (x) => x.omsInkl + x.mersalgInkl), db: sum(l, (x) => x.resultat), aarsager: [...new Set(l.flatMap(aarsagerFor))] }))
    .filter((k) => k.db < 0).sort((a, b) => a.db - b.db).slice(0, 8);

  const dage = Math.round((Date.parse(r.til) - Date.parse(r.fra)) / 864e5) + 1;
  const maaneder = dage / (365.25 / 12);
  return {
    fra: r.fra, til: r.til, dage, ordrer: ordrer.length, oms, omsInkl, moms: omsInkl - oms, mersalg, mersalgInkl,
    loen: loenI, loenBooket, ubooket, ubookedeTimer, koersel, km: rundKm(sum(alle, (x) => x.f.km)), materialer, udlaeg, udlaegInkl, refusion, refusionInkl,
    nyOmgang, nyOmgangAntal: rekl.length, gebyr, db, faste: Math.round(s.faste * maaneder),
    vandfald: { omsInkl: omsInkl + mersalgInkl, refusionInkl, momsNetto: (omsInkl + mersalgInkl - oms - mersalg) - (refusionInkl - refusion), oms: oms + mersalg, refusion,
      loen: loenI, koersel, materialer, udlaeg, db },
    steamere, pakker, nyOmgangRaekke, tilvalg, tilvalgDb, afrunding,
    rabatter, udenKodeDbOrdre: udenKode.length ? div(sum(udenKode, (x) => x.resultat), udenKode.length) : null,
    byer, tabsordrer, tabAntal: tab.length, tabSum: -sum(tab, (x) => x.resultat), aarsager, kunder,
    antaget: { loen: steamerIds.some((id) => s.loenAntaget(id)), km: steamerIds.some((id) => s.kmAntaget(id)), materialer: s.materialerAntaget, faste: !o.faste.length },
    betaling: r.betaling, rykker: r.rykker, churn: r.medlemmer.aktiveStart ? r.medlemmer.opsagt / r.medlemmer.aktiveStart : null, medlemmer: r.medlemmer,
  };
}

function grupper<T>(l: T[], n: (x: T) => string): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const x of l) { const k = n(x); (m.get(k) ?? m.set(k, []).get(k)!).push(x); }
  return [...m];
}

// ---------- Prognose ----------
export type Prognose = { maaned: string; booket: number; booketAntal: number; resten: number; prognose: number; lav: number; hoej: number; kilde: 'booket' | 'saeson' | 'snit' };
/** Median af tallene (null uden tal). */
const median = (l: number[]) => { if (!l.length) return null; const s = [...l].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
export const maanedAf = (dato: string) => dato.slice(0, 7);
export const naesteMaaned = (m: string, n = 1) => { const [y, mm] = m.split('-').map(Number); const t = y * 12 + mm - 1 + n; return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`; };

/** Måneder med pålidelig historik: data fra hele måneden (ikke kun de sidste dage), og bookingernes oprettelse ser ægte ud
 *  (er næsten alt "oprettet" efter måneden, er det importerede data eller testdata – så kan man ikke se, hvornår der blev booket). */
const paalidelig = (k: { kendt: number; endelig: number }) => k.endelig > 0 && k.kendt / k.endelig >= 0.15 && k.kendt / k.endelig <= 1;

/** Niveauet for en måned (ekskl. moms) ud fra historikken alene: samme måned sidste år × årets vækst (sæson) – ellers
 *  snittet af de seneste tre hele måneder før `foer`. 0 = ingen historik. */
function niveau(h: AnalyseHistorik, m: string, foer: string): { basis: number; kilde: 'saeson' | 'snit' | null } {
  const mnd = new Map(h.maaneder.map((x) => [x.maaned, x.oms]));
  const fra = h.byer.fra ? maanedAf(h.byer.fra) : null;
  const hel = (x: string) => !!fra && x > fra;   // første måned med data er sjældent hel
  const aar = foer.slice(0, 4);
  let iAar = 0, sidst = 0;
  for (let x = `${aar}-01`; x < foer; x = naesteMaaned(x)) if (hel(x) && hel(`${Number(aar) - 1}${x.slice(4)}`)) { iAar += mnd.get(x) ?? 0; sidst += mnd.get(`${Number(aar) - 1}${x.slice(4)}`) ?? 0; }
  const ly = `${Number(m.slice(0, 4)) - 1}${m.slice(4)}`;
  if (hel(ly) && (mnd.get(ly) ?? 0) > 0) return { basis: (mnd.get(ly) ?? 0) * (sidst > 0 && iAar > 0 ? iAar / sidst : 1), kilde: 'saeson' };
  const seneste = [1, 2, 3].map((i) => naesteMaaned(foer, -i)).filter((x) => hel(x) && (mnd.get(x) ?? 0) > 0).map((x) => mnd.get(x)!);
  return seneste.length ? { basis: sum(seneste, (x) => x) / seneste.length, kilde: 'snit' } : { basis: 0, kilde: null };
}

/**
 * Prognose for denne måned og resten af året (ekskl. moms):
 *  • denne måned: det bookede (udført + kommende) uden medlemmer × hvor meget månederne plejer at vokse fra samme dag til
 *    månedens slut (median af de seneste pålidelige måneder) + medlemmernes besøg (det bookede eller det forventede ud fra
 *    aktive medlemskaber, hvis det er mere). "Resten" begrænses af niveauet (sæson eller snit) + 15 % – og prognosen er
 *    aldrig under det, der allerede er booket. Uden pålidelig historik regnes kun med det bookede;
 *  • senere måneder: niveauet – aldrig under det, der allerede er booket. Intervallet er ±10 %.
 * Tjekket mod sidste hele måned med backtest() (samme metode med kun de data, der fandtes på samme dag i den måned).
 */
export function prognose(h: AnalyseHistorik): Prognose[] {
  const nu = maanedAf(h.idag);
  const aar = nu.slice(0, 4);
  const ratioer = h.kendt.filter(paalidelig).map((k) => k.endelig / k.kendt);
  const r = median(ratioer) ?? 1, rLav = ratioer.length ? Math.min(...ratioer) : 1, rHoej = ratioer.length ? Math.max(...ratioer) : 1;
  const ud: Prognose[] = [];
  for (let m = nu; m.slice(0, 4) === aar; m = naesteMaaned(m)) {
    const b = h.booket.find((x) => x.maaned === m) ?? { oms: 0, antal: 0, medlem: 0, moms: 0 };
    const medlem = Math.max(b.medlem, h.medlemForventet.find((x) => x.maaned === m)?.oms ?? 0);
    const ikke = b.oms - b.medlem;
    const { basis, kilde } = niveau(h, m, nu);
    const loft = basis ? Math.max(b.oms, basis * 1.15) : Infinity;
    const begr = (x: number) => Math.round(Math.max(b.oms, Math.min(x, loft)));
    if (m === nu) {
      const p = begr(ikke * r + medlem);
      // Intervallet: laveste–højeste vækst blandt månederne (med mindst to at sammenligne) – ellers ±10 %
      const flere = ratioer.length > 1 && ikke * r + medlem <= loft;   // ramt af loftet: ±10 % i stedet
      ud.push({ maaned: m, booket: b.oms, booketAntal: b.antal, prognose: p, resten: p - b.oms,
        lav: Math.min(p, flere ? begr(ikke * rLav + medlem) : Math.max(b.oms, Math.round(p * 0.9))),
        hoej: Math.max(p, flere ? begr(ikke * rHoej + medlem) : Math.round(Math.min(p * 1.1, Math.max(p, loft)))), kilde: 'booket' });
    } else {
      const p = Math.round(Math.max(b.oms, basis, ikke + medlem));
      ud.push({ maaned: m, booket: b.oms, booketAntal: b.antal, prognose: p, resten: p - b.oms, lav: Math.max(b.oms, Math.round(p * 0.9)), hoej: Math.round(p * 1.1), kilde: kilde ?? 'snit' });
    }
  }
  return ud;
}

/** Backtest: prognosen for sidste hele måned med kun det, der var booket på samme dag i den måned (uden medlemmer), mod det
 *  faktiske. null = ingen pålidelig historik at teste på. */
export function backtest(h: AnalyseHistorik): { maaned: string; forudsagt: number; faktisk: number; afvigelse: number } | null {
  const sidste = [...h.kendt].sort((a, b) => a.maaned.localeCompare(b.maaned)).filter(paalidelig).at(-1);
  if (!sidste) return null;
  const ratioer = h.kendt.filter((k) => k !== sidste && k.maaned < sidste.maaned && paalidelig(k)).map((k) => k.endelig / k.kendt);
  const r = median(ratioer) ?? 1;
  const { basis } = niveau(h, sidste.maaned, sidste.maaned);
  const forudsagt = Math.round(Math.max(sidste.kendt, Math.min(sidste.kendt * r, basis ? Math.max(sidste.kendt, basis * 1.15) : Infinity)));
  return { maaned: sidste.maaned, forudsagt, faktisk: sidste.endelig, afvigelse: (forudsagt - sidste.endelig) / sidste.endelig };
}

/** Momsandelen i omsætningen (til at vise prognosen inkl. moms). */
export const momsAndel = (h: AnalyseHistorik) => { const o = sum(h.maaneder, (m) => m.oms); return o ? sum(h.maaneder, (m) => m.moms) / o : 0.25; };

// ---------- Datakilder ----------
export type KildeStatus = 'har' | 'delvist' | 'estimat' | 'mangler';
export type Datakilde = { data: string; brugesTil: string; status: KildeStatus; hvor: string; saetOp: string };
export function datakilder(st: AnalyseStatus, o: AnalyseOpsaetning, idag: string): Datakilde[] {
  const tal = (n: number) => n.toLocaleString('da-DK');
  const del = (har: number, af: number): KildeStatus => (af > 0 && har >= af ? 'har' : har > 0 ? 'delvist' : 'mangler');
  const loen = o.timeloen != null ? 'har' : del(st.steamereLoen, st.steamere);
  const bil = del(st.steamereBil, st.steamere);
  const mat = o.materialer != null && st.ydelserMateriale >= st.ydelser ? 'har' : del(st.ydelserMateriale, st.ydelser);
  const aarSiden = st.bookingerFra ? ` siden ${st.bookingerFra.slice(0, 4)}` : '';
  return [
    { data: 'Bookinger, priser, rabatter, tilvalg', brugesTil: 'Omsætning, pakker, byer', status: st.bookinger ? 'har' : 'mangler', hvor: `Bookinger (${tal(st.bookinger)}${aarSiden})`, saetOp: '–' },
    { data: 'Mersalg', brugesTil: 'Medarbejdere', status: 'har', hvor: 'Tilvalg tilføjet af steameren', saetOp: '–' },
    { data: 'Klager og ny omgang', brugesTil: 'Tab på klager', status: st.klager && !st.refusioner ? 'delvist' : 'har', hvor: 'Kundecenter + bookinger fra reklamation', saetOp: st.refusioner ? '–' : 'Refusionsbeløb gemmes på sagen' },
    { data: 'Betalinger og rykkere', brugesTil: 'Betalingsdisciplin', status: 'har', hvor: 'Bogholderi › Betalinger og Rykkere', saetOp: '–' },
    { data: 'Medlemskaber', brugesTil: 'Churn, kundelevetidsværdi', status: st.medlemFra && st.medlemFra <= plusDage(idag, -365) ? 'har' : st.medlemskaber ? 'delvist' : 'mangler', hvor: 'Medlemskaber + medlemshistorik', saetOp: st.medlemFra && st.medlemFra <= plusDage(idag, -365) ? '–' : 'Importér Frisbii-historikken' },
    { data: 'Arbejdstider og udlæg', brugesTil: 'Udnyttelse, udlæg', status: st.arbejdstider ? 'har' : 'mangler', hvor: 'Team › Steamere og Udlæg', saetOp: st.arbejdstider ? '–' : 'Arbejdstider pr. steamer' },
    { data: 'Faktisk tid på opgaven', brugesTil: 'Løn pr. ordre', status: 'delvist', hvor: '"Ankommet" og kundens underskrift', saetOp: 'Gem tidspunkt for "Færdig"' },
    { data: 'Kørte kilometer', brugesTil: 'Kørsel pr. ordre', status: 'estimat', hvor: 'Afstand mellem postnumre', saetOp: 'GPS-spor fra steamer-appen' },
    { data: 'Timeløn pr. steamer', brugesTil: 'Løn, DB pr. time', status: loen, hvor: loen === 'mangler' ? 'Løndata har kun CPR og konto' : `${st.steamereLoen} af ${st.steamere} steamere`, saetOp: loen === 'har' ? '–' : 'Timeløn, pension og feriepenge pr. steamer' },
    { data: 'Brændstof og strøm', brugesTil: 'Kørsel pr. medarbejder', status: bil, hvor: bil === 'mangler' ? 'Kun udlæg i kategorien Brændstof' : `${st.steamereBil} af ${st.steamere} biler`, saetOp: bil === 'har' ? '–' : 'Bil pr. steamer: benzin/diesel/el, forbrug og pris' },
    { data: 'Materialer', brugesTil: 'DB pr. pakke', status: mat, hvor: mat === 'mangler' ? 'Kun samlede indkøb i Udlæg' : `${st.ydelserMateriale} af ${st.ydelser} pakker`, saetOp: mat === 'har' ? '–' : 'Standardforbrug pr. pakke og tilvalg' },
    { data: 'Faste omkostninger', brugesTil: 'Resultat', status: st.faste ? 'har' : 'mangler', hvor: st.faste ? `${st.faste} poster` : '–', saetOp: st.faste ? '–' : 'Husleje, biler, forsikring pr. måned' },
  ];
}

// ---------- Databasens svar → typerne her (sbAnalyse.ts og tests/db/analyse.test.ts) ----------
type R = unknown[];
const tal = (x: unknown) => (x == null ? 0 : Number(x));
/** analyse_fakta som kort liste (pixie_analyse_raekke) → Fakta. */
export function faktaFraRaekke(r: R): Fakta {
  return { id: tal(r[0]), dato: String(r[1]), status: r[2] as Booking['status'], steamer_id: (r[3] as string) ?? null, ydelse_id: String(r[4]), postnr: String(r[5]),
    by: String(r[6]), kunde_id: String(r[7]), reklamation_for: r[8] == null ? null : tal(r[8]), medlem: !!r[9], kupon: (r[10] as string) ?? null, ny_kunde: !!r[11],
    udeblev: !!r[12], pris: tal(r[13]), moms: tal(r[14]), mersalg: tal(r[15]), mersalg_moms: tal(r[16]), gebyr: tal(r[17]), rabat: tal(r[18]),
    tilvalg: ((r[19] as R[]) ?? []).map((t) => [String(t[0]), tal(t[1]), tal(t[2])] as [string, number, number]), minutter: tal(r[20]), km: tal(r[21]), oprettet: String(r[22]) };
}
/** analyse_periode → AnalyseRaa. */
export function analyseRaaFraDb(x: Record<string, unknown>, fra: string, til: string): AnalyseRaa {
  const l = (n: string) => ((x[n] as R[]) ?? []);
  const b = (x.betaling as R) ?? [], rk = (x.rykker as R) ?? [], m = (x.medlemmer as R) ?? [];
  return {
    fra, til, idag: String(x.idag),
    // Kunden er et nummer i listen 'kunder', og oprettet er ikke med (bruges kun i historikken)
    fakta: l('fakta').map((r) => faktaFraRaekke([...r.slice(0, 7), (x.kunder as string[])[tal(r[7])], ...r.slice(8), ''])),
    refusioner: l('refusioner').map((r) => ({ booking_id: r[0] == null ? null : tal(r[0]), steamer_id: (r[1] as string) ?? null, beloeb: tal(r[2]) })),
    udlaeg: l('udlaeg').map((r) => ({ steamer_id: (r[0] as string) ?? null, kategori: String(r[1]), beloeb: tal(r[2]) })),
    steamere: l('steamere').map((r) => ({ id: String(r[0]), navn: String(r[1]), aktiv: !!r[2], farve: String(r[3] ?? '#2C473D') })),
    arbejdstider: l('arbejdstider').map((r) => ({ steamer_id: String(r[0]), ugedag: tal(r[1]), start: tal(r[2]), slut: tal(r[3]) })),
    saerligeDage: l('saerlige').map((r, i) => ({ id: 's' + i, steamer_id: String(r[0]), dato: String(r[1]), start: tal(r[2]), slut: tal(r[3]) })),
    fravaer: l('fravaer').map((r, i) => ({ id: 'f' + i, steamer_id: String(r[0]), fra: String(r[1]), til: String(r[2]), type: 'fri' as const })),
    lukkedage: (x.lukkedage as string[] ?? []).map(String),
    ydelser: l('ydelser').map((r) => ({ id: String(r[0]), navn: String(r[1]) })),
    tilvalg: l('tilvalg').map((r) => ({ id: String(r[0]), navn: String(r[1]), varighed: tal(r[2]) })),
    betaling: { tilTiden: tal(b[0]), dage1: tal(b[1]), dage15: tal(b[2]), dage30: tal(b[3]), snitDage: b[4] == null ? null : tal(b[4]) },
    rykker: { rykker1: tal(rk[0]), rykker2: tal(rk[1]), klar: tal(rk[2]), udestaaende30: tal(rk[3]), kunder30: tal(rk[4]), hosRykker2: tal(rk[5]) },
    medlemmer: { aktiveStart: tal(m[0]), opsagt: tal(m[1]) },
  };
}
/** analyse_sammenlign → Sammenlign. */
export const sammenlignFraDb = (x: R[], fra: string, til: string): Sammenlign =>
  ({ fra, til, byer: (x ?? []).map((r) => ({ postnr: String(r[0]), by: String(r[1]), oms: tal(r[2]), moms: tal(r[3]), antal: tal(r[4]) })) });
/** analyse_historik → AnalyseHistorik. */
export function historikFraDb(x: Record<string, unknown>): AnalyseHistorik {
  const l = (n: string) => ((x[n] as R[]) ?? []);
  const s = (x.status ?? {}) as Record<string, unknown>, k = (x.klv as R) ?? [], by = (x.byer as R) ?? [];
  return {
    idag: String(x.idag),
    maaneder: l('maaneder').map((r) => ({ maaned: String(r[0]), oms: tal(r[1]), moms: tal(r[2]), antal: tal(r[3]) })),
    booket: l('booket').map((r) => ({ maaned: String(r[0]), oms: tal(r[1]), moms: tal(r[2]), antal: tal(r[3]), medlem: tal(r[4]) })),
    kendt: l('kendt').map((r) => ({ maaned: String(r[0]), kendt: tal(r[1]), endelig: tal(r[2]) })),
    medlemForventet: l('medlem_forventet').map((r) => ({ maaned: String(r[0]), oms: tal(r[1]) })),
    churn: l('churn').map((r) => ({ maaned: String(r[0]), opsagt: tal(r[1]), aktive: tal(r[2]) })),
    klv: { alle: tal(k[0]), medlem: tal(k[1]), oevrige: tal(k[2]) },
    byer: { antal: tal(by[0]), fra: (by[1] as string) ?? null },
    status: {
      bookinger: tal(s.bookinger), bookingerFra: (s.bookinger_fra as string) ?? null, mersalg: tal(s.mersalg), klager: tal(s.klager), nyOmgang: tal(s.ny_omgang),
      refusioner: tal(s.refusioner), betalinger: tal(s.betalinger), rykkere: tal(s.rykkere), medlemskaber: tal(s.medlemskaber), medlemFra: (s.medlem_fra as string) ?? null,
      arbejdstider: tal(s.arbejdstider), udlaeg: tal(s.udlaeg), ankommet: tal(s.ankommet), underskrift: tal(s.underskrift), steamere: tal(s.steamere),
      steamereLoen: tal(s.steamere_loen), steamereBil: tal(s.steamere_bil), ydelser: tal(s.ydelser), ydelserMateriale: tal(s.ydelser_materiale), faste: tal(s.faste),
    },
  };
}
const talEllerNull = (x: unknown) => (x == null || x === '' ? null : Number(x));
/** analyse_opsaetning → AnalyseOpsaetning. */
export function opsaetningFraDb(x: Record<string, unknown>): AnalyseOpsaetning {
  return {
    timeloen: talEllerNull(x.timeloen), tillaeg: talEllerNull(x.tillaeg), kmPris: talEllerNull(x.km_pris), materialer: talEllerNull(x.materialer),
    steamere: ((x.steamere as Record<string, unknown>[]) ?? []).map((s) => ({ steamer_id: String(s.steamer_id), timeloen: talEllerNull(s.timeloen), tillaeg: talEllerNull(s.tillaeg),
      drivmiddel: (s.drivmiddel as Drivmiddel) ?? null, forbrug: talEllerNull(s.forbrug), energipris: talEllerNull(s.energipris) })),
    materialeSatser: ((x.materialer_satser as Record<string, unknown>[]) ?? []).map((m) => ({ art: m.art as 'ydelse' | 'tilvalg', ref_id: String(m.ref_id), beloeb: Number(m.beloeb) })),
    faste: ((x.faste as Record<string, unknown>[]) ?? []).map((f) => ({ id: String(f.id), navn: String(f.navn), beloeb: Number(f.beloeb) })),
  };
}
/** AnalyseOpsaetning → gem_analyse_opsaetning. */
export const opsaetningTilDb = (o: AnalyseOpsaetning) => ({
  timeloen: o.timeloen, tillaeg: o.tillaeg, km_pris: o.kmPris, materialer: o.materialer,
  steamere: o.steamere, materialer_satser: o.materialeSatser, faste: o.faste.map((f) => ({ navn: f.navn, beloeb: f.beloeb })),
});
